import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import {
  ArrowLeft, User, FileText, BookOpen, Upload, Trash2, Download,
  Edit3, Save, X, Calendar, Briefcase, Phone, Mail, MapPin, Plus, Eye, Wallet, UserMinus,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import EmployeePaymentsTab from "@/components/EmployeePaymentsTab";
import EmployeeLeaverTab from "@/components/EmployeeLeaverTab";
import { formatDate } from "@/lib/dates";

const DOC_TYPES = [
  { v: "employment_contract", label: "Employment Contract" },
  { v: "dbs_certificate",     label: "DBS Disclosure Certificate" },
  { v: "reference",           label: "Reference" },
  { v: "medical_assessment",  label: "Medical / Health Assessment" },
  { v: "offer_letter",        label: "Offer Letter" },
  { v: "job_description",     label: "Job Description" },
  { v: "nda",                 label: "NDA / Confidentiality" },
  { v: "background_check",    label: "Background Check" },
  { v: "right_to_work",       label: "Right to Work Verification" },
  { v: "training_certificate", label: "Training Certificate" },
  { v: "incident_report",     label: "Incident Report" },
  { v: "disciplinary_record", label: "Disciplinary Record" },
  { v: "custom",              label: "Custom (specify)" },
];

export default function EmployeeProfilePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const isHRAdmin = ["super_admin", "hr_admin"].includes(user?.role);
  const isSuper = user?.role === "super_admin";
  const [emp, setEmp] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "profile";
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/employees/${id}`);
      setEmp(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load employee");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  if (loading) return <div className="text-center py-20 text-[var(--cc-text-soft)]">Loading employee…</div>;
  if (!emp) return <div className="text-center py-20 text-[var(--cc-text-soft)]">Employee not found.</div>;

  const initials = (emp.full_name || "").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="fade-up">
      <div className="mb-4">
        <Link to="/app/employees" className="btn-ghost text-xs inline-flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to employees
        </Link>
      </div>
      <div className="cc-card p-6 mb-6 flex items-start gap-4">
        <div className="w-14 h-14 rounded-full bg-[var(--cc-sage)] text-white flex items-center justify-center text-lg font-bold">
          {initials || "?"}
        </div>
        <div className="flex-1">
          <div className="label-eyebrow mb-1">{emp.department || "Employee"}</div>
          <h1 className="font-display text-3xl font-bold" data-testid="employee-page-name">{emp.full_name}</h1>
          <div className="flex flex-wrap gap-4 text-sm text-[var(--cc-text-soft)] mt-1">
            <span className="inline-flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {emp.job_title || "—"}</span>
            <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {emp.work_email}</span>
            {emp.phone_personal && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {emp.phone_personal}</span>}
            <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Started {formatDate(emp.start_date) || "—"}</span>
          </div>
        </div>
        <span className={`pill ${emp.employment_status === "leaver" ? "bg-red-100 text-red-800" : emp.status === "active" ? "pill-success" : "pill-info"}`}>{emp.employment_status === "leaver" ? "leaver" : emp.status?.replace("_", " ")}</span>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap" data-testid="employee-tabs">
        {[
          { k: "profile",   label: "Profile",      icon: User },
          { k: "documents", label: "HR Documents", icon: FileText },
          { k: "policies",  label: "Policies",     icon: BookOpen },
          { k: "payments",  label: "Payments",     icon: Wallet },
          ...(isHRAdmin ? [{ k: "leaver", label: "Leaver", icon: UserMinus }] : []),
        ].map(({ k, label, icon: Icon }) => (
          <button
            key={k}
            className={`px-4 py-2 rounded-full text-sm font-medium transition inline-flex items-center gap-1.5 ${tab === k ? "bg-[var(--cc-sage)] text-white" : "bg-[var(--cc-surface)] hover:bg-[var(--cc-surface-hover)]"}`}
            onClick={() => { setTab(k); setSearchParams(k === "profile" ? {} : { tab: k }); }}
            data-testid={`employee-tab-${k}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "profile"   && <ProfileTab emp={emp} isHRAdmin={isHRAdmin} onSaved={load} />}
      {tab === "documents" && <DocumentsTab emp={emp} isHRAdmin={isHRAdmin} />}
      {tab === "policies"  && <PoliciesTab emp={emp} />}
      {tab === "payments"  && <EmployeePaymentsTab employeeId={emp.id} viewerRole={user?.role} />}
      {tab === "leaver"    && isHRAdmin && <EmployeeLeaverTab emp={emp} onSaved={load} />}
      {tab === "leaver"    && !isHRAdmin && (
        <div className="cc-card p-8 text-center text-[var(--cc-text-soft)]" data-testid="leaver-rbac-denied">
          You do not have permission to view leaver records.
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="label-eyebrow mb-1">{label}</div>
      <div className="text-sm font-medium">{value || <span className="italic text-[var(--cc-text-soft)]">Not set</span>}</div>
    </div>
  );
}

function ProfileTab({ emp, isHRAdmin, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => ({
    title: emp.title || "", first_name: emp.first_name || "", middle_name: emp.middle_name || "",
    last_name: emp.last_name || "", preferred_name: emp.preferred_name || "",
    work_email: emp.work_email || "", personal_email: emp.personal_email || "",
    phone_personal: emp.phone_personal || "", phone_work: emp.phone_work || "",
    date_of_birth: emp.date_of_birth || "", gender: emp.gender || "",
    address: emp.address || {},
    emergency_contact_name: emp.emergency_contact_name || "",
    emergency_contact_relationship: emp.emergency_contact_relationship || "",
    emergency_contact_phone: emp.emergency_contact_phone || "",
    job_title: emp.job_title || "", department: emp.department || "",
    contract_type: emp.contract_type || "", hours_per_week: emp.hours_per_week || "",
    salary: emp.salary || "", manager_name: emp.manager_name || "",
    line_manager_user_id: emp.line_manager_user_id || "",
    start_date: emp.start_date || "",
    bank: emp.bank || {},
    bio: emp.bio || "",
  }));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.hours_per_week === "") delete payload.hours_per_week;
      else payload.hours_per_week = Number(payload.hours_per_week);
      await api.patch(`/employees/${emp.id}`, payload);
      toast.success("Profile saved");
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (!editing) {
    return (
      <div className="space-y-5">
        {isHRAdmin && (
          <div className="flex justify-end">
            <button className="btn-secondary text-sm inline-flex items-center gap-1.5" onClick={() => setEditing(true)} data-testid="edit-employee-button">
              <Edit3 className="w-3.5 h-3.5" /> Edit profile
            </button>
          </div>
        )}
        <Section title="Personal Details">
          <Field label="Title" value={emp.title} />
          <Field label="First name" value={emp.first_name} />
          <Field label="Middle name" value={emp.middle_name} />
          <Field label="Last name" value={emp.last_name} />
          <Field label="Preferred name" value={emp.preferred_name} />
          <Field label="Date of birth" value={emp.date_of_birth} />
          <Field label="Gender" value={emp.gender} />
          <Field label="Marital status" value={emp.marital_status} />
        </Section>
        <Section title="Contact">
          <Field label="Work email" value={emp.work_email} />
          <Field label="Personal email" value={emp.personal_email} />
          <Field label="Phone (personal)" value={emp.phone_personal} />
          <Field label="Phone (work)" value={emp.phone_work} />
          <Field label="Address line 1" value={emp.address?.line_1} />
          <Field label="City" value={emp.address?.city} />
          <Field label="Postcode" value={emp.address?.postcode} />
        </Section>
        <Section title="Emergency Contact">
          <Field label="Name" value={emp.emergency_contact_name} />
          <Field label="Relationship" value={emp.emergency_contact_relationship} />
          <Field label="Phone" value={emp.emergency_contact_phone} />
          <Field label="Address" value={emp.emergency_contact_address} />
        </Section>
        <Section title="Employment">
          <Field label="Job title" value={emp.job_title} />
          <Field label="Department" value={emp.department} />
          <Field label="Manager" value={emp.manager_name} />
          <Field label="Contract type" value={emp.contract_type} />
          <Field label="Hours/week" value={emp.hours_per_week} />
          <Field label="Salary" value={emp.salary} />
          <Field label="Pay frequency" value={emp.pay_frequency?.replace("_"," ")} />
          <Field label="Start date" value={emp.start_date ? formatDate(emp.start_date) : null} />
        </Section>
        {isHRAdmin && (
          <Section title="Bank & Payroll (HR Admin only)">
            <Field label="Account holder" value={emp.bank?.account_holder} />
            <Field label="Account number" value={emp.bank?.account_number} />
            <Field label="Sort code" value={emp.bank?.sort_code} />
            <Field label="NI number" value={emp.bank?.national_insurance_number} />
            <Field label="Tax code" value={emp.bank?.tax_code} />
          </Section>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-end gap-2">
        <button className="btn-ghost text-sm" onClick={() => setEditing(false)}><X className="w-3.5 h-3.5" /> Cancel</button>
        <button className="btn-primary text-sm inline-flex items-center gap-1.5" onClick={save} disabled={busy} data-testid="save-employee-button">
          <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save"}
        </button>
      </div>
      <EditSection title="Personal Details">
        <Input label="Title" value={form.title} onChange={(v) => setForm({...form,title:v})} />
        <Input label="First name" value={form.first_name} onChange={(v) => setForm({...form,first_name:v})} />
        <Input label="Middle name" value={form.middle_name} onChange={(v) => setForm({...form,middle_name:v})} />
        <Input label="Last name" value={form.last_name} onChange={(v) => setForm({...form,last_name:v})} />
        <Input label="Preferred name" value={form.preferred_name} onChange={(v) => setForm({...form,preferred_name:v})} testId="preferred-name-input" />
        <Input label="Date of birth" type="date" value={form.date_of_birth?.slice(0,10) || ""} onChange={(v) => setForm({...form,date_of_birth:v})} />
        <Input label="Gender" value={form.gender} onChange={(v) => setForm({...form,gender:v})} />
      </EditSection>
      <EditSection title="Contact">
        <Input label="Work email" value={form.work_email} onChange={(v) => setForm({...form,work_email:v})} />
        <Input label="Personal email" value={form.personal_email} onChange={(v) => setForm({...form,personal_email:v})} />
        <Input label="Phone (personal)" value={form.phone_personal} onChange={(v) => setForm({...form,phone_personal:v})} />
        <Input label="Phone (work)" value={form.phone_work} onChange={(v) => setForm({...form,phone_work:v})} />
        <Input label="Address line 1" value={form.address?.line_1 || ""} onChange={(v) => setForm({...form, address: {...form.address, line_1: v}})} />
        <Input label="City" value={form.address?.city || ""} onChange={(v) => setForm({...form, address: {...form.address, city: v}})} />
        <Input label="Postcode" value={form.address?.postcode || ""} onChange={(v) => setForm({...form, address: {...form.address, postcode: v}})} />
      </EditSection>
      <EditSection title="Emergency Contact">
        <Input label="Name" value={form.emergency_contact_name} onChange={(v) => setForm({...form,emergency_contact_name:v})} />
        <Input label="Relationship" value={form.emergency_contact_relationship} onChange={(v) => setForm({...form,emergency_contact_relationship:v})} />
        <Input label="Phone" value={form.emergency_contact_phone} onChange={(v) => setForm({...form,emergency_contact_phone:v})} />
      </EditSection>
      {isHRAdmin && <>
        <EditSection title="Employment">
          <Input label="Job title" value={form.job_title} onChange={(v) => setForm({...form,job_title:v})} />
          <Input label="Department" value={form.department} onChange={(v) => setForm({...form,department:v})} />
          <Input label="Manager (text)" value={form.manager_name} onChange={(v) => setForm({...form,manager_name:v})} />
          <LineManagerSelect value={form.line_manager_user_id} onChange={(v) => setForm({...form,line_manager_user_id:v})} />
          <ContractTypeSelect value={form.contract_type} onChange={(v) => setForm({...form,contract_type:v})} />
          <Input label="Hours/week" value={form.hours_per_week} onChange={(v) => setForm({...form,hours_per_week:v})} />
          {!isHourlyContract(form.contract_type) && (
            <Input label="Annual salary (£)" value={form.salary} onChange={(v) => setForm({...form,salary:v})} testId="salary-input" />
          )}
          {isHourlyContract(form.contract_type) && (
            <div className="cc-card p-3 bg-[var(--cc-surface)] text-xs text-[var(--cc-text-soft)]">
              Hourly contract — no annual salary needed. Pay for each 4-week period is agreed with payroll.
            </div>
          )}
          <Input label="Start date" type="date" value={(form.start_date || "").slice(0,10)} onChange={(v) => setForm({...form,start_date:v})} testId="start-date-input" />
        </EditSection>
        <EditSection title="Bank & Payroll">
          <Input label="Account holder" value={form.bank?.account_holder || ""} onChange={(v) => setForm({...form, bank: {...form.bank, account_holder: v}})} />
          <Input label="Account number" value={form.bank?.account_number || ""} onChange={(v) => setForm({...form, bank: {...form.bank, account_number: v}})} />
          <Input label="Sort code" value={form.bank?.sort_code || ""} onChange={(v) => setForm({...form, bank: {...form.bank, sort_code: v}})} />
          <Input label="NI number" value={form.bank?.national_insurance_number || ""} onChange={(v) => setForm({...form, bank: {...form.bank, national_insurance_number: v}})} />
          <Input label="Tax code" value={form.bank?.tax_code || ""} onChange={(v) => setForm({...form, bank: {...form.bank, tax_code: v}})} />
        </EditSection>
      </>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="cc-card p-5">
      <h3 className="font-display text-lg font-bold mb-4">{title}</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{children}</div>
    </div>
  );
}
function EditSection({ title, children }) {
  return (
    <div className="cc-card p-5">
      <h3 className="font-display text-lg font-bold mb-4">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}
function Input({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="label-eyebrow block mb-1">{label}</label>
      <input className="cc-input" type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}

function LineManagerSelect({ value, onChange }) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    api.get("/employees/line-manager-options").then((r) => setOpts(r.data || [])).catch(() => {});
  }, []);
  return (
    <div>
      <label className="label-eyebrow block mb-1">Line manager (system user)</label>
      <select className="cc-select" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid="line-manager-select">
        <option value="">— None —</option>
        {opts.map((o) => (
          <option key={o.id} value={o.id}>{o.name} ({o.role})</option>
        ))}
      </select>
      <div className="text-[11px] text-[var(--cc-text-soft)] mt-1">Used for appraisals — the manager scorecard will be assigned to this user.</div>
    </div>
  );
}

const HOURLY_CONTRACT_VALUES = ["zero_hours", "bank", "relief", "variable_hours", "casual"];
const CONTRACT_TYPE_OPTIONS = [
  { v: "permanent",       label: "Permanent (fixed annual salary)" },
  { v: "fixed_term",      label: "Fixed-term (fixed salary)" },
  { v: "zero_hours",      label: "Zero-hour" },
  { v: "bank",            label: "Bank" },
  { v: "relief",          label: "Relief" },
  { v: "variable_hours",  label: "Variable hours" },
  { v: "casual",          label: "Casual" },
];

function isHourlyContract(ct) {
  if (!ct) return false;
  const norm = String(ct).trim().toLowerCase().replace(/\s+/g, "_");
  return HOURLY_CONTRACT_VALUES.includes(norm);
}

function ContractTypeSelect({ value, onChange }) {
  // Accept legacy free-text values gracefully — if value isn't in the list, show it as a custom option
  const known = CONTRACT_TYPE_OPTIONS.some((o) => o.v === value);
  return (
    <div>
      <label className="label-eyebrow block mb-1">Contract type</label>
      <select className="cc-select" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid="contract-type-select">
        <option value="">— Select —</option>
        {CONTRACT_TYPE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        {value && !known && <option value={value}>{value} (legacy)</option>}
      </select>
      <div className="text-[11px] text-[var(--cc-text-soft)] mt-1">Hourly contracts (zero-hour/bank/relief/etc.) skip the salary field — an hourly rate is used instead.</div>
    </div>
  );
}

function DocumentsTab({ emp, isHRAdmin }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/employees/${emp.id}/documents`);
      setRows(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [emp.id]);

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.filename}"?`)) return;
    await api.delete(`/employees/${emp.id}/documents/${d.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      {isHRAdmin && (
        <div className="flex justify-end mb-4">
          <button className="btn-primary text-sm inline-flex items-center gap-1.5" onClick={() => setShowUpload(true)} data-testid="upload-doc-button">
            <Upload className="w-3.5 h-3.5" /> Upload document
          </button>
        </div>
      )}
      <div className="cc-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--cc-surface)]">
            <tr className="text-left">
              <th className="px-5 py-3 label-eyebrow">Type</th>
              <th className="px-5 py-3 label-eyebrow">Filename</th>
              <th className="px-5 py-3 label-eyebrow">Uploaded</th>
              <th className="px-5 py-3 label-eyebrow">Expiry</th>
              <th className="px-5 py-3 label-eyebrow text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="5" className="text-center py-10 text-[var(--cc-text-soft)]">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="5" className="text-center py-10 text-[var(--cc-text-soft)]" data-testid="empty-docs">No documents uploaded yet.</td></tr>}
            {rows.map((d) => (
              <tr key={d.id} className="border-t border-[var(--cc-border)]" data-testid={`hr-doc-row-${d.id}`}>
                <td className="px-5 py-3">
                  {(DOC_TYPES.find((t) => t.v === d.document_type)?.label) || d.document_type}
                  {d.custom_type_label && <span className="ml-1 text-[11px] text-[var(--cc-text-soft)]">({d.custom_type_label})</span>}
                </td>
                <td className="px-5 py-3 text-xs">{d.filename}</td>
                <td className="px-5 py-3 text-xs text-[var(--cc-text-soft)]">{d.upload_date?.slice(0, 10)}</td>
                <td className="px-5 py-3 text-xs text-[var(--cc-text-soft)]">{d.expiry_date?.slice(0, 10) || "—"}</td>
                <td className="px-5 py-3 text-right">
                  <a href={`${apiBase}/api/employees/${emp.id}/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="btn-ghost p-1.5" title="Download" data-testid={`download-doc-${d.id}`}>
                    <Download className="w-4 h-4" />
                  </a>
                  {isHRAdmin && (
                    <button className="btn-ghost p-1.5" title="Delete" onClick={() => remove(d)} data-testid={`delete-doc-${d.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showUpload && (
        <DocUploadModal employeeId={emp.id} onClose={() => setShowUpload(false)} onSaved={() => { setShowUpload(false); load(); }} />
      )}
    </div>
  );
}

function DocUploadModal({ employeeId, onClose, onSaved }) {
  const [type, setType] = useState("employment_contract");
  const [customLabel, setCustomLabel] = useState("");
  const [expiry, setExpiry] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) return toast.error("Choose a file");
    if (file.size > 20 * 1024 * 1024) return toast.error("File exceeds 20 MB");
    if (type === "custom" && !customLabel.trim()) return toast.error("Custom label required");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("document_type", type);
      if (type === "custom") form.append("custom_type_label", customLabel.trim());
      if (expiry) form.append("expiry_date", expiry);
      if (notes) form.append("notes", notes);
      form.append("file", file);
      await api.post(`/employees/${employeeId}/documents`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} widthClass="max-w-lg" testId="hr-doc-upload-modal">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><Upload className="w-5 h-5" /> Upload HR Document</h2>
        <button className="btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>
      <div className="space-y-3">
        <div>
          <label className="label-eyebrow block mb-1">Document type *</label>
          <select className="cc-select" value={type} onChange={(e) => setType(e.target.value)} data-testid="doc-type-select">
            {DOC_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
          </select>
        </div>
        {type === "custom" && (
          <div>
            <label className="label-eyebrow block mb-1">Custom label *</label>
            <input className="cc-input" value={customLabel} onChange={(e) => setCustomLabel(e.target.value)} data-testid="custom-label-input" />
          </div>
        )}
        <div>
          <label className="label-eyebrow block mb-1">Expiry date</label>
          <input type="date" className="cc-input" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="expiry-date-input" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Notes</label>
          <textarea className="cc-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">File * (max 20 MB)</label>
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.heic,.webp" data-testid="doc-file-input" />
          {file && <div className="text-[11px] text-[var(--cc-text-soft)] mt-1">{file.name} · {(file.size/1024).toFixed(1)} KB</div>}
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={submit} disabled={busy} data-testid="doc-upload-submit">{busy ? "Uploading…" : "Upload"}</button>
      </div>
    </Modal>
  );
}

function PoliciesTab({ emp }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/policies`).then(async ({ data }) => {
      // Fetch acks for each policy for this employee
      const enriched = await Promise.all((data || []).map(async (p) => {
        const { data: acks } = await api.get(`/policies/${p.id}/acknowledgments`);
        const myAck = (acks || []).find((a) => a.employee_id === emp.id);
        return { ...p, ack: myAck };
      }));
      setRows(enriched);
    }).finally(() => setLoading(false));
  }, [emp.id]);

  return (
    <div className="cc-card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[var(--cc-surface)]">
          <tr className="text-left">
            <th className="px-5 py-3 label-eyebrow">Policy</th>
            <th className="px-5 py-3 label-eyebrow">Category</th>
            <th className="px-5 py-3 label-eyebrow">Version</th>
            <th className="px-5 py-3 label-eyebrow">Status</th>
            <th className="px-5 py-3 label-eyebrow">Acknowledged</th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan="5" className="text-center py-10 text-[var(--cc-text-soft)]">Loading…</td></tr>}
          {!loading && rows.length === 0 && <tr><td colSpan="5" className="text-center py-10 text-[var(--cc-text-soft)]">No policies set up yet.</td></tr>}
          {rows.map((p) => (
            <tr key={p.id} className="border-t border-[var(--cc-border)]" data-testid={`emp-policy-row-${p.id}`}>
              <td className="px-5 py-3 font-medium">{p.name}</td>
              <td className="px-5 py-3 text-xs">{p.category?.replace("_", " ")}</td>
              <td className="px-5 py-3 text-xs">{p.version}</td>
              <td className="px-5 py-3">
                {!p.ack && <span className="pill pill-info text-[10px]">Not assigned</span>}
                {p.ack?.status === "pending" && <span className="pill pill-warning text-[10px]">Pending</span>}
                {p.ack?.status === "acknowledged" && <span className="pill pill-success text-[10px]">Acknowledged</span>}
              </td>
              <td className="px-5 py-3 text-xs text-[var(--cc-text-soft)]">{p.ack?.acknowledged_at?.slice(0, 10) || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
